import { useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';

import { isVisitorRoute } from './kit-scope';
import { runThemeTransition } from './theme-transition';

/**
 * Theme of the TOOL zone (panel, settings, dialogs). Light by default; the
 * choice lives in this device's storage. The visitor routes never take it: the
 * public tour keeps its own look whatever the creator picked.
 *
 * `index.html` repeats `readTheme` + `isVisitorRoute` in an inline script so
 * the first paint already has the class (no light flash for dark users). Keep
 * the storage key and the visitor test in sync with it.
 */
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'u360.theme';

/** Anything but an explicit "dark" is light (missing, corrupted, old values). */
export function parseTheme(raw: string | null | undefined): Theme {
  return raw === 'dark' ? 'dark' : 'light';
}

export function readTheme(): Theme {
  try {
    return parseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'light';
  }
}

function writeTheme(theme: Theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Private mode / blocked storage: the choice just lasts for this visit.
  }
}

/** Whether <html> carries `.dark`: only a dark choice on a tool route. */
export function shouldUseDark(theme: Theme, pathname: string, embed: boolean): boolean {
  return theme === 'dark' && !isVisitorRoute(pathname, embed);
}

const themeAtom = atom<Theme>(typeof window === 'undefined' ? 'light' : readTheme());

/**
 * Current theme and its toggle (the top bar's moon / sun). `toggle` takes the
 * center of the pressed button so the change can reveal from there.
 */
export function useTheme(): { theme: Theme; toggle: (origin?: { x: number; y: number }) => void } {
  const [theme, setTheme] = useAtom(themeAtom);
  const toggle = (origin?: { x: number; y: number }) => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    writeTheme(next);
    // flushSync: the view transition snapshots the page as soon as this returns, so `.dark` must already be on <html>.
    runThemeTransition(() => flushSync(() => setTheme(next)), origin);
  };
  return { theme, toggle };
}

/**
 * Puts `.dark` on <html> on the tool routes when the theme is dark, and takes
 * it off on the visitor routes. On <html> (like `data-kit`) because menus,
 * dialogs and toasts are portaled to <body>. Layout effect: the route's first
 * paint already has it.
 */
export function useThemeScope(embed: boolean) {
  const { pathname } = useLocation();
  const theme = useAtomValue(themeAtom);
  const dark = shouldUseDark(theme, pathname, embed);
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);
}
